import { normalizeMerchant } from '../services/merchantName';

/**
 * Which template a merchant gets, as a name — without importing a single component.
 *
 * CPAYMENT-1786: this lives apart from `templates/index.ts` on purpose. Analytics needs the same
 * answer the renderer uses, and importing it from the index created a cycle
 * (`buildBasePayload` → `templates/index` → templates → screens → `services/receiptUpload` →
 * `buildBasePayload`). It resolves today only because every use sits inside a function body; a
 * module-level use of either binding would turn it into a boot-time crash. It also dragged the
 * whole template tree into the module graph of any leaf-service test.
 *
 * The merchant list here is the single source of truth: `templates/index.ts` builds its map from it.
 *
 * `onepage` is out of rotation — no merchant resolves to it, and it is reachable only from the
 * preview; the name stays because historical telemetry carries it.
 */
export type TemplateName = 'default' | 'pinco' | 'onepage' | 'gatev1' | 'quasi';

export const TEMPLATE_MERCHANTS: Array<{ merchants: string[]; name: TemplateName }> = [
  { merchants: ['pinco', 'vocxod'], name: 'pinco' },
  { merchants: ['chcmanual', 'betrun'], name: 'default' },
];

/** Methods paid on the partner's checkout. The second name is the method's former one. */
const QUASI_METHODS = ['P2P QUASI ECOM RUB', 'MATCHING QUASI ECOM RUB'];

/**
 * The payment method a link promises before the ticket is read: the backend marks a quasi deposit's
 * link with `s=q`. It only lets the page open in the right template at once; the ticket's own method
 * replaces it as soon as the ticket is read.
 */
export const paymentMethodFromLink = (search: string = window.location.search): string | null => {
  try {
    return new URLSearchParams(search).get('s') === 'q' ? QUASI_METHODS[0] : null;
  } catch {
    return null;
  }
};

/**
 * Priority: the quasi method → personal template → `PayPageGateV1` for everyone else. The method
 * outranks the merchant: a personal template is a card-transfer page, which this method never shows.
 * Without a merchant name no template can be chosen, so we stay on the safe DefaultTemplate.
 *
 * The answer depends on the ticket's merchant and method alone, so reopening the link or coming back
 * from the bank cannot land the same client on a different screen.
 *
 * This is also what analytics asks for the template it reports. Split the decision in two and the
 * rendered screen and the reported one drift apart, which makes any figure sliced by template a
 * claim about a screen nobody was shown.
 */
export const resolveTemplateName = (merchant?: string, paymentMethod?: string | null): TemplateName => {
  if (paymentMethod && QUASI_METHODS.includes(paymentMethod)) {
    return 'quasi';
  }
  const name = normalizeMerchant(merchant);
  if (!name) {
    return 'default';
  }
  const match = TEMPLATE_MERCHANTS.find((e) => e.merchants.includes(name));
  if (match) {
    return match.name;
  }
  return 'gatev1';
};

/**
 * Whether the template follows the payment through its review: it shows processing while the ticket
 * is in `FILES_VERIFICATION` and ends on the real terminal status. The page asks this to decide
 * what it does on the template's behalf — read the real status after a confirmation and keep
 * polling during review. The other templates end the client's path at the confirmation and are
 * left exactly as they are.
 */
export const followsPaymentReview = (name: TemplateName): boolean => name === 'gatev1';
